import React, { useContext, useEffect } from 'react';
import Image from '../../atoms/Image';
import Header from '../../atoms/Header';
import Button from '../../atoms/Button';
import {
  eventParamAttributes,
  EventParamTypes,
  inViewEventAttributes,
} from '@spotify-internal/touchdown-analytics';
import DirectionContext from '../../../configuration/context/direction';
import { getDaysRemaining } from '../../../helpers/helpers';

import {
  ButtonContainer,
  TweakedPremiumHeroWrapper,
  OfferEndsSpan,
  TweakedPremiumHeader,
  MessageWrapper,
  Wrapper,
  CustomLegalTerms,
} from './components';
import MarketContext from '../../../configuration/context/market';
import {
  componentViewEvent,
  linkClickEvent,
} from '../../../lib/tracking/eventPayloadDefinitions';
import { trackEvent } from '../../../lib/tracking';
import UserContext from '../../../configuration/context/user';
import { useInView } from 'react-intersection-observer';
import { getCookie } from '../../../lib/cookie-utils';

export type TweakedPremiumHeroProps = {
  background: string;
  gradientColor: string;
  proposition: {
    fullWidth?: boolean;
    assets: {
      default: React.ComponentProps<typeof Image>;
      rtl?: React.ComponentProps<typeof Image>;
    };
  };
  eventParams?: EventParamTypes;
  header: React.ComponentProps<typeof Header>;
  offerEnds: string | null;
  offerDateEnds: number | null;
  buttons: React.ComponentProps<typeof Button>[];
  legalCopy: React.ComponentProps<typeof CustomLegalTerms>;
};

export function TweakedPremiumHero(props: TweakedPremiumHeroProps) {
  const {
    background,
    gradientColor,
    proposition,
    header,
    eventParams,
    offerEnds,
    offerDateEnds,
    buttons,
    legalCopy,
  } = props;

  const daysRemaining = offerDateEnds ? getDaysRemaining(offerDateEnds) : null;
  const direction = useContext(DirectionContext);

  let assets = proposition.assets.default.sources;
  if (direction === 'rtl' && proposition.assets.rtl) {
    assets = proposition.assets.rtl.sources;
  }

  const { ref, inView } = useInView({
    fallbackInView: true,
    threshold: 0.6,
    triggerOnce: true,
  });

  const market = useContext(MarketContext);
  const user = useContext(UserContext);

  // When button is clicked, trigger a linkClickEvent
  const handleHeroCTAClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const payload = {
      resource_name: 'premium',
      link_url: e.currentTarget.getAttribute('href') as string,
      country: market?.country ?? 'unknown',
      locale: market?.language ?? 'unknown',
      full_url: window.location.href,
      timestamp: Math.floor(Date.now() / 1000),
      correlation_id: getCookie('sp_t') ?? 'unknown',
      is_evergreen: market?.isEvergreen,
      eligibility: user?.eligibility,
      component_view: 'premium_tweak_hero',
    };

    trackEvent(
      linkClickEvent(payload, {
        authorize: user.authenticated,
      }),
    );
    e.stopPropagation();
  };

  // On  load initialize MarktingResourceViewEvent
  useEffect(() => {
    if (inView === true) {
      const payload = {
        resource_name: 'premium',
        component_name: 'premium_tweak_hero',
        country: market?.country ?? 'unknown',
        locale: market?.language ?? 'unknown',
        full_url: window.location.href,
        correlation_id: getCookie('sp_t') ?? 'unknown',
        is_evergreen: market?.isEvergreen,
        eligibility: user?.eligibility,
      };

      trackEvent(
        componentViewEvent(payload, {
          authorize: user.authenticated,
        }),
      );
    }
  }, [market, user, inView]);

  return (
    <TweakedPremiumHeroWrapper
      imageNeedFullWidth={proposition.fullWidth}
      background={{ color: background }}
      backgroundSources={assets}
      gradientColor={gradientColor}
      isEvergreen={market?.isEvergreen}
      eligibility={user?.eligibility}
      direction={direction}
      style={!!proposition.fullWidth ? { paddingTop: '0' } : {}}
      {...eventParamAttributes({ ...eventParams, componentType: 'promo' })}
      {...inViewEventAttributes()}
    >
      <Wrapper ref={ref}>
        <MessageWrapper imageNeedFullWidth={proposition.fullWidth}>
          {header && <TweakedPremiumHeader {...header} usePrimaryHeading />}
          {!!offerEnds && !!daysRemaining && (
            <OfferEndsSpan>
              {offerEnds.replace('%DAYSREMAINING%', daysRemaining)}
            </OfferEndsSpan>
          )}
          <ButtonContainer>
            {buttons &&
              buttons.map(
                (
                  {
                    children,
                    href,
                    label,
                    backgroundColor,
                    insertMarket,
                    $borderValues,
                  },
                  index,
                ) => (
                  <Button
                    key={`button-${index}`}
                    children={children}
                    href={href}
                    label={label}
                    backgroundColor={backgroundColor}
                    insertMarket={insertMarket}
                    $borderValues={$borderValues}
                    onClick={handleHeroCTAClick}
                  />
                ),
              )}
          </ButtonContainer>
          <CustomLegalTerms {...legalCopy} />
        </MessageWrapper>
      </Wrapper>
    </TweakedPremiumHeroWrapper>
  );
}

export default TweakedPremiumHero;
